import { Link, usePage } from "@inertiajs/react"
import { UserPlus } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { InlineInput } from "@/components/ui/inline-input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  dinerProfileGroups,
  ProfileSidebar,
} from "@/components/features/profiles/profile-sidebar"
import { ThemeSelector } from "@/features/user/theme-selector"
import { patchInline } from "@/lib/inline-update"
import type { DinerSummary } from "@/features/user/types"
import { t } from "@/lib/i18n"

type SharedUser = {
  name: string
  email: string
  initials: string
  avatar: string | null
  anonymous?: boolean
}

type Props = {
  /** Mirrors User::DINER_NAME_MAX_LENGTH so the cap is not duplicated here. */
  maxNameLength: number
}

export default function UserProfilesShow({ maxNameLength }: Props) {
  const { user, diner } = usePage<{ user: SharedUser | null; diner?: DinerSummary }>().props

  if (!user) return null

  const anonymous = Boolean(user.anonymous)
  // Same signal the shell uses: `diner` is null exactly when there is no active
  // visit (User::DinerSerialization#serialize_diner).
  const hasVisit = Boolean(diner)

  return (
    <UserLayout>
      <div className="mx-auto w-full max-w-lg px-4 py-8" data-testid="profile-show-view">
        <div className="flex flex-col items-center gap-3 text-center">
          <Avatar className="size-20">
            <AvatarImage src={user.avatar || ""} alt={user.name} />
            <AvatarFallback className="text-xl">{user.initials}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" data-testid="profile-show-name">
              {user.name}
            </h1>
            {!anonymous && (
              <p className="text-sm text-muted-foreground" data-testid="profile-show-email">
                {user.email}
              </p>
            )}
          </div>
        </div>

        {anonymous && (
          <div className="mt-6 rounded-2xl border bg-card p-4 shadow-xs" data-testid="profile-guest-cta">
            <p className="text-sm font-semibold tracking-tight">
              {t("ui.react.components.features.profiles.guest.title")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("ui.react.components.features.profiles.guest.hint")}
            </p>

            {/* A guest's name is what their table-mates read on every bill line,
                so renaming has to be reachable without an account. It commits
                inline — one light field, no form to submit. */}
            <div className="mt-4 space-y-1">
              <Label htmlFor="profile-guest-name">
                {t("ui.react.components.features.profiles.guest.name_label")}
              </Label>
              <InlineInput
                id="profile-guest-name"
                value={user.name}
                maxLength={maxNameLength}
                autoComplete="given-name"
                data-testid="profile-guest-name-input"
                onSubmit={(name) => patchInline("/profile", { name })}
              />
              <p className="text-xs text-muted-foreground">
                {t("ui.react.components.features.profiles.guest.name_hint")}
              </p>
            </div>

            <Separator className="my-4" />

            <Button asChild className="w-full">
              <Link href="/registration/new">
                <UserPlus />
                {t("ui.react.components.features.profiles.guest.register")}
              </Link>
            </Button>
          </div>
        )}

        <div className="mt-6">
          <ThemeSelector />
        </div>

        <Separator className="my-6" />

        {/* One list, not two. Language and time zone moved behind Configuración,
            which leaves this screen as what it is: the way out to everything
            else. A guest gets the entries that are theirs to use. */}
        <ProfileSidebar groups={dinerProfileGroups({ anonymous, hasVisit })} />
      </div>
    </UserLayout>
  )
}
